/**
 * EmployeePicker — Mitarbeiter-Auswahl-Combobox (UX_KONZEPT.md §2.2).
 *
 * WAI-ARIA-Combobox-Pattern: role="combobox" am Input, role="listbox"/"option"
 * an der Liste, aria-activedescendant für Tastatur-Hervorhebung.
 *
 * Datenquelle: GET /api/v1/employees (app-eigener Proxy, niemals die
 * Personal-API direkt aus dem Browser — CORS/Token-Audience, §2.3). Solange
 * der Endpoint mit 503 antwortet (Personal-Anbindung nicht konfiguriert,
 * ANNAHME in backend/app/services/personal_client.py), degradiert dieser
 * Picker sichtbar zu einem Freitextfeld für die employee_id — kein stiller
 * Fehler.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"

export interface EmployeeOption {
  id: string
  name: string
  kuerzel: string | null
  aktiv: boolean
}

export interface EmployeePickerProps {
  value: string | null
  onChange: (id: string | null, employee?: EmployeeOption) => void
  label?: string
  required?: boolean
  disabled?: boolean
}

function anzeige(e: Pick<EmployeeOption, "name" | "kuerzel">): string {
  return e.kuerzel ? `${e.name} (${e.kuerzel})` : e.name
}

async function fetchEmployees(): Promise<EmployeeOption[]> {
  const resp = await api.get<EmployeeOption[]>("/employees", { params: { nur_aktive: false } })
  return resp.data
}

export function EmployeePicker({
  value,
  onChange,
  label = "Mitarbeiter",
  required = false,
  disabled = false,
}: EmployeePickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [zeigeInaktive, setZeigeInaktive] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: employees, isLoading, isError, refetch } = useQuery({
    queryKey: ["employees"],
    queryFn: fetchEmployees,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  // Degradation: Personal-Anbindung (noch) nicht konfiguriert (503) oder
  // sonstiger Ladefehler -> Freitextfeld statt Combobox (kein stiller Fehler).
  const degraded = isError

  const selected = useMemo(
    () => employees?.find((e) => e.id === value) ?? null,
    [employees, value],
  )

  useEffect(() => {
    if (!open) {
      setQuery(selected ? anzeige(selected) : "")
    }
  }, [open, selected])

  const gefiltert = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("de")
    return (employees ?? [])
      .filter((e) => zeigeInaktive || e.aktiv || e.id === value)
      .filter(
        (e) =>
          !q ||
          e.name.toLocaleLowerCase("de").includes(q) ||
          (e.kuerzel ?? "").toLocaleLowerCase("de").includes(q),
      )
  }, [employees, query, zeigeInaktive, value])

  useEffect(() => {
    setActiveIdx(0)
  }, [query, zeigeInaktive])

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [])

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [activeIdx])

  function waehle(e: EmployeeOption) {
    onChange(e.id, e)
    setQuery(anzeige(e))
    setOpen(false)
  }

  function leeren() {
    onChange(null)
    setQuery("")
    inputRef.current?.focus()
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setOpen(true)
      setActiveIdx((i) => Math.min(gefiltert.length - 1, i + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const item = gefiltert[activeIdx]
      if (item) waehle(item)
    } else if (e.key === "Escape") {
      setOpen(false)
      setQuery(selected ? anzeige(selected) : "")
    }
  }

  if (degraded) {
    return (
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
        <input
          type="text"
          required={required}
          disabled={disabled}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="Mitarbeiter-ID"
          className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/60 focus:border-slate-400"
        />
        <p className="mt-1 text-xs text-amber-700">
          Mitarbeiterliste ist derzeit nicht verfügbar (Personal-Anbindung noch nicht
          konfiguriert) — bitte die Mitarbeiter-ID direkt eintragen.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1 block text-xs font-medium text-slate-700">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls="employee-picker-listbox"
          aria-activedescendant={
            open && gefiltert[activeIdx] ? `employee-option-${gefiltert[activeIdx].id}` : undefined
          }
          disabled={disabled}
          required={required}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onKeyDown={onKeyDown}
          placeholder="Name oder Kürzel suchen…"
          className="w-full rounded-md border border-slate-300 px-3 py-1.5 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/60 focus:border-slate-400 disabled:bg-slate-100"
        />
        {value && !disabled && (
          <button
            type="button"
            aria-label="Auswahl leeren"
            onClick={leeren}
            disabled={required}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
          <div
            ref={listRef}
            id="employee-picker-listbox"
            role="listbox"
            aria-label={label}
            className="max-h-56 overflow-y-auto py-1"
          >
            {isLoading && (
              <div className="space-y-2 px-3 py-2">
                <div className="h-4 w-3/4 animate-pulse rounded-md bg-slate-200" />
                <div className="h-4 w-1/2 animate-pulse rounded-md bg-slate-200" />
              </div>
            )}
            {!isLoading && gefiltert.length === 0 && (
              <div className="px-3 py-3 text-center text-sm text-slate-400">
                Keine Mitarbeiter gefunden.
              </div>
            )}
            {!isLoading &&
              gefiltert.map((e, idx) => (
                <div
                  key={e.id}
                  id={`employee-option-${e.id}`}
                  data-idx={idx}
                  role="option"
                  aria-selected={e.id === value}
                  onMouseDown={(ev) => {
                    ev.preventDefault()
                    waehle(e)
                  }}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={`cursor-pointer px-3 py-1.5 text-sm ${
                    idx === activeIdx ? "bg-slate-100" : ""
                  } ${!e.aktiv ? "text-slate-400" : "text-slate-900"}`}
                >
                  {anzeige(e)}
                  {!e.aktiv && " — inaktiv"}
                </div>
              ))}
          </div>
          <label className="flex items-center gap-1.5 border-t border-slate-100 px-3 py-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={zeigeInaktive}
              onChange={(e) => setZeigeInaktive(e.target.checked)}
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            />
            Inaktive anzeigen
          </label>
        </div>
      )}
    </div>
  )
}
